import { UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Separator,
  Switch,
} from '@ais-ui/ui-core';
import React from 'react';
import { GroundingDetectorFormValues } from './groundingForm';

interface GroundingFormProps {
  form: UseFormReturn<GroundingDetectorFormValues>;
}

export const GroundingFields = ({ form }: GroundingFormProps) => {
  const { messages } = useLocalization();

  // Watch the useRemainsGroundedCheck value to conditionally render validateMinutes
  const useRemainsGroundedCheck = form.watch('useRemainsGroundedCheck');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="useSogDecrease"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-2">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsGroundingUseSogDecrease}
                </FormLabel>
                <InfoIconTooltip className="ml-1">
                  {messages.eventdetectorsGroundingUseSogDecreaseToolTip}
                </InfoIconTooltip>
              </div>
              <Separator></Separator>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  id="event-grounding-sog-decrease-switch"
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div></div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="useRemainsGroundedCheck"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-2">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsGroundingUseRemainsGroundedCheck}
                </FormLabel>
                <InfoIconTooltip className="ml-1">
                  {
                    messages.eventdetectorsGroundingUseRemainsGroundedCheckToolTip
                  }
                </InfoIconTooltip>
              </div>
              <Separator></Separator>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  id="event-grounding-remains-grounded-check-switch"
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {useRemainsGroundedCheck ? (
          <FormField
            control={form.control}
            name="validateMinutes"
            render={({ field }) => (
              <FormItem className="flex flex-col space-y-2">
                <div className="flex justify-between mr-2">
                  <FormLabel>
                    {messages.eventdetectorsGroundingValidateMinutes}
                  </FormLabel>
                  <InfoIconTooltip className="ml-1">
                    {messages.eventdetectorsGroundingValidateMinutesToolTip}
                  </InfoIconTooltip>
                </div>
                <Separator></Separator>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    className="w-48"
                    type="number"
                    id="event-grounding-validate-minutes-input"
                    value={field.value}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const limitedValue = Math.min(value, 1440);
                      field.onChange(limitedValue);
                    }}
                    max={1440}
                    min={0}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <div></div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="useShallowWaters"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-2">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsGroundingUseShallowWaters}
                </FormLabel>
                <InfoIconTooltip className="ml-1">
                  {messages.eventdetectorsGroundingUseShallowWatersToolTip}
                </InfoIconTooltip>
              </div>
              <Separator></Separator>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  id="event-grounding-shallow-waters-switch"
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="filterPorts"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-2">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsGroundingFilterPorts}
                </FormLabel>
                <InfoIconTooltip className="ml-1">
                  {messages.eventdetectorsGroundingFilterPortsToolTip}
                </InfoIconTooltip>
              </div>
              <Separator></Separator>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  id="event-grounding-filter-ports-switch"
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="filterAnchorageAreas"
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-2">
              <div className="flex justify-between mr-2">
                <FormLabel>
                  {messages.eventdetectorsGroundingFilterAnchorageAreas}
                </FormLabel>
                <InfoIconTooltip className="ml-1">
                  {messages.eventdetectorsGroundingFilterAnchorageAreasToolTip}
                </InfoIconTooltip>
              </div>
              <Separator></Separator>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  id="event-grounding-filter-anchorage-areas-switch"
                  checked={field.value}
                  onCheckedChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
